import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

const PRIMARY = "#E5A325";
const BG = "hsl(220, 20%, 7%)";
const BG_CARD = "hsl(220, 18%, 10%)";
const TEXT = "#e6e0d4";
const TEXT_MUTED = "#808090";
const GLASS_BG = "hsla(220, 18%, 12%, 0.7)";
const GLASS_BORDER = "hsla(220, 15%, 25%, 0.5)";
const GRADIENT_CTA = `linear-gradient(135deg, ${PRIMARY}, #d4922a)`;
const FONT_DISPLAY = "'Space Grotesk', 'Inter', sans-serif";
const FONT_BODY = "'Inter', sans-serif";

const inputStyle = {
    width: "100%", padding: "14px 16px", borderRadius: 12,
    border: `1px solid ${GLASS_BORDER}`,
    background: "hsla(220,18%,8%,0.8)", color: TEXT,
    fontSize: "0.95rem", outline: "none", fontFamily: FONT_BODY,
};
const labelStyle = {
    color: TEXT_MUTED, fontSize: "0.78rem", fontWeight: 600,
    textTransform: "uppercase", letterSpacing: 1,
    display: "block", marginBottom: 8, fontFamily: FONT_BODY,
};

const IconBattery = ({ size = 24 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="6" width="18" height="12" rx="2" /><line x1="23" y1="10" x2="23" y2="14" /><line x1="7" y1="10" x2="7" y2="14" /><line x1="11" y1="10" x2="11" y2="14" />
    </svg>
);

const ResetPassword = () => {
    document.title = "Redefinir Senha | Oplena";

    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(""); setSuccess("");

        if (!token) { setError("Token de redefinição ausente ou inválido."); return; }
        if (password.length < 6) { setError("A senha deve ter pelo menos 6 caracteres."); return; }
        if (password !== confirm) { setError("As senhas não coincidem."); return; }

        setLoading(true);
        try {
            const res = await fetch(process.env.REACT_APP_URL_API + "auth/reset-password", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ token, new_password: password }),
            });
            const data = await res.json();
            if (res.ok) {
                setSuccess("Senha redefinida com sucesso!");
            } else {
                setError(data?.message || "Token inválido ou expirado.");
            }
        } catch {
            setError("Erro de conexão. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: "100vh", background: BG,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, fontFamily: FONT_BODY,
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');
                input::placeholder { color: ${TEXT_MUTED} !important; }
                input:focus { border-color: ${PRIMARY} !important; }
            `}</style>

            <div style={{
                display: "flex", maxWidth: 960, width: "100%", borderRadius: 24, overflow: "hidden",
                boxShadow: "0 8px 32px hsla(0,0%,0%,0.4)", border: `1px solid ${GLASS_BORDER}`,
            }}>
                {/* ═══ LEFT - Branding ═══ */}
                <div style={{
                    flex: "1 1 420px", minHeight: 480,
                    background: GLASS_BG, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                    padding: "48px 40px", display: "flex", flexDirection: "column", justifyContent: "space-between",
                    position: "relative", overflow: "hidden", borderRight: `1px solid ${GLASS_BORDER}`,
                }}>
                    <div style={{ position: "absolute", top: -80, right: -80, width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, hsla(43,96%,56%,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
                    <div style={{ position: "absolute", bottom: -60, left: -60, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, hsla(38,100%,50%,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 40 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 14, background: GRADIENT_CTA, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 20px hsla(43,96%,56%,0.3)", color: BG }}>
                                <IconBattery size={24} />
                            </div>
                            <span style={{ fontFamily: FONT_DISPLAY, fontSize: "1.6rem", fontWeight: 700 }}>
                                <span style={{ color: PRIMARY }}>ie</span><span style={{ color: TEXT }}>Volt</span>
                            </span>
                        </div>
                        <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "2rem", margin: 0, lineHeight: 1.2, color: "hsl(45, 10%, 85%)" }}>
                            Rede de Recarga
                        </h2>
                        <h3 style={{ color: PRIMARY, fontWeight: 400, fontSize: "1.4rem", margin: "8px 0 0", fontStyle: "italic" }}>
                            Inteligente & Conectada
                        </h3>
                    </div>

                    <div>
                        <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                            {[
                                { icon: <i className="fa-solid fa-lock" />, text: "Nova senha segura" },
                                { icon: <i className="fa-solid fa-check" />, text: "Processo simples" },
                                { icon: <i className="fa-solid fa-bolt" />, text: "Acesso imediato" },
                            ].map((item, i) => (
                                <div key={i} style={{
                                    flex: 1, padding: "14px 8px", textAlign: "center", borderRadius: 12,
                                    background: "hsla(220,18%,15%,0.5)", border: `1px solid ${GLASS_BORDER}`,
                                }}>
                                    <div style={{ fontSize: "1.3rem", marginBottom: 4 }}>{item.icon}</div>
                                    <div style={{ color: TEXT_MUTED, fontSize: "0.7rem" }}>{item.text}</div>
                                </div>
                            ))}
                        </div>
                        <p style={{ color: "hsla(45,10%,90%,0.35)", fontSize: "0.8rem", fontStyle: "italic", margin: 0, borderLeft: `2px solid hsla(43,96%,56%,0.3)`, paddingLeft: 12 }}>
                            "Escolha uma senha forte para manter sua conta segura."
                        </p>
                    </div>
                </div>

                {/* ═══ RIGHT - Form ═══ */}
                <div style={{
                    flex: "1 1 420px", background: BG_CARD,
                    padding: "48px 40px", display: "flex", flexDirection: "column", justifyContent: "center",
                }}>
                    <h2 style={{ fontFamily: FONT_DISPLAY, color: PRIMARY, fontWeight: 700, fontSize: "1.8rem", marginBottom: 6 }}>
                        Redefinir senha
                    </h2>
                    <p style={{ color: TEXT_MUTED, fontSize: "0.9rem", marginBottom: 32 }}>
                        Digite sua nova senha abaixo.
                    </p>

                    {success ? (
                        <div>
                            <div style={{
                                background: "hsla(140,70%,40%,0.12)", border: "1px solid hsla(140,70%,40%,0.3)",
                                color: "#4ade80", borderRadius: 12, padding: "20px 24px", marginBottom: 24,
                                fontSize: "0.95rem", textAlign: "center",
                            }}>
                                <i className="fa-solid fa-check"></i> {success}
                            </div>
                            <Link to="/login" style={{
                                display: "block", width: "100%", padding: "15px 0", borderRadius: 14,
                                background: GRADIENT_CTA, color: BG, fontWeight: 700, fontSize: "1rem",
                                textAlign: "center", textDecoration: "none",
                                boxShadow: "0 4px 20px hsla(43,96%,56%,0.25)", fontFamily: FONT_BODY,
                            }}>
                                Ir para o Login
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div style={{ marginBottom: 16 }}>
                                <label style={labelStyle}>Nova Senha</label>
                                <input style={inputStyle} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Digite a nova senha" required minLength={6} />
                            </div>

                            <div style={{ marginBottom: 20 }}>
                                <label style={labelStyle}>Confirmar Senha</label>
                                <input style={inputStyle} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirme a nova senha" required minLength={6} />
                            </div>

                            {error && (
                                <div style={{
                                    background: "hsla(0,70%,50%,0.12)", border: "1px solid hsla(0,70%,50%,0.3)",
                                    color: "#f87171", borderRadius: 12, padding: "14px 18px", marginBottom: 20, fontSize: "0.85rem",
                                }}>
                                    {error}
                                </div>
                            )}

                            <button type="submit" disabled={loading} style={{
                                width: "100%", padding: "15px 0", borderRadius: 14, border: "none",
                                cursor: loading ? "not-allowed" : "pointer",
                                background: GRADIENT_CTA, color: BG, fontWeight: 700, fontSize: "1rem",
                                fontFamily: FONT_BODY,
                                boxShadow: "0 4px 20px hsla(43,96%,56%,0.25)",
                                opacity: loading ? 0.7 : 1, transition: "opacity 0.2s",
                            }}>
                                {loading ? "Redefinindo..." : "Redefinir Senha"}
                            </button>
                        </form>
                    )}

                    <div style={{ textAlign: "center", marginTop: 24 }}>
                        <Link to="/login" style={{ color: PRIMARY, fontSize: "0.85rem", textDecoration: "none", fontFamily: FONT_BODY }}>
                            ← Voltar ao Login
                        </Link>
                    </div>

                    <div style={{ textAlign: "center", marginTop: 40, color: TEXT_MUTED, fontSize: "0.75rem" }}>
                        © {new Date().getFullYear()} Oplena. Todos os direitos reservados.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
